import { BellIcon } from '@radix-ui/react-icons';
import { useOnFocusOrClickOutside } from '@src/hooks/useOnFocusOrClickOutside';
import { PropsWithChildren, useRef, useState, MouseEvent } from 'react';
import {
  useListNotifications,
  useMarkAllNotificationsAsRead,
  usePatchNotification,
} from '@src/api/useNotifications';
import { useNotificationLink } from '@src/hooks/useNotificationLink';
import { cn, formatDate } from '@repo/ui/utils';
import { Button } from '@repo/ui/components/Button';
import { getFullName } from '@src/lib/utils';
import { useNavigate } from 'react-router-dom';

const notificationClasses = [
  { threshold: 99, positionClass: '-right-4', widthClass: 'w-7' },
  { threshold: 9, positionClass: '-right-3', widthClass: 'w-6' },
  { threshold: 0, positionClass: '-right-1', widthClass: 'w-4' },
];

function getRightPositionClass(notificationCount: number) {
  const matchingClass = notificationClasses.find(
    (pc) => notificationCount > pc.threshold
  );
  return matchingClass ? matchingClass.positionClass : '-right-1';
}

function getWidthClass(notificationCount: number) {
  const matchingClass = notificationClasses.find(
    (pc) => notificationCount > pc.threshold
  );
  return matchingClass ? matchingClass.widthClass : 'w-4';
}

export function NotificationsBadge() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { getNotificationLink } = useNotificationLink();
  const ref = useRef<HTMLButtonElement>(null);

  const { data: notifications } = useListNotifications('unread');
  const patchNotificationMutation = usePatchNotification();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const clickOutsideRef = useOnFocusOrClickOutside((event) => {
    if (ref.current && ref.current === event.target) return;
    if (ref.current && ref.current.contains(event.target as Node)) return;
    setIsPopupOpen(false);
  });

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const markAsRead = (notificationId: string) => {
    patchNotificationMutation.mutate({
      id: notificationId,
      patchRequest: { read: true },
    });
  };

  return (
    <div className={cn('relative')}>
      <Button
        aria-label="Notifications"
        variant="ghost"
        className={cn(
          'hover:bg-accent',
          'px-2',
          'py-2',
          'rounded-lg',
          'h-10',
          'text-left',
          'font-normal'
        )}
        ref={ref}
        onClick={togglePopup}
      >
        <div className={cn('relative', 'inline-block')}>
          <div className={cn('flex', 'items-center', 'justify-center')}>
            <BellIcon className={cn('w-5', 'h-5')} />
          </div>
          {notifications && notifications?.length > 0 && (
            <div
              className={cn(
                'absolute',
                '-top-1',
                getRightPositionClass(notifications.length),
                'flex',
                'justify-center',
                'items-center',
                getWidthClass(notifications.length),
                'h-4',
                'bg-destructive',
                'rounded-full',
                'text-background',
                'text-[0.65rem]'
              )}
            >
              <span>
                {notifications.length > 99 ? '99+' : notifications.length}
              </span>
            </div>
          )}
        </div>
      </Button>
      {isPopupOpen && (
        <div
          className={cn(
            'absolute',
            'top-full',
            'right-0',
            'mt-2',
            'w-[350px] md:w-[520px]',
            'bg-background',
            'shadow-lg',
            'p-4',
            'rounded-md',
            'border'
          )}
          ref={clickOutsideRef}
        >
          <div
            className={cn(
              'flex',
              'items-center',
              'justify-between',
              'gap-2',
              'border-b',
              'border-border',
              'mb-3',
              'pb-3'
            )}
          >
            <p className={cn('text-xl', 'font-medium')}>Notifications</p>
            {notifications && notifications.length > 0 && (
              <Button
                variant="ghost"
                className={cn('h-7', 'text-xs', 'min-w-max')}
                disabled={markAllAsReadMutation.isLoading}
                onClick={() => markAllAsReadMutation.mutate()}
              >
                Mark all as read
              </Button>
            )}
          </div>
          {notifications && notifications.length === 0 ? (
            <div className={cn('pt-4')} aria-label="Notifications empty state">
              There are no new notifications at this time.
            </div>
          ) : (
            <div
              className={cn(
                'max-h-[100px] md:max-h-[550px]',
                'overflow-y-auto'
              )}
              aria-label="Notifications popup"
            >
              <ul>
                {notifications &&
                  notifications.map((notification) => {
                    const notificationLink = getNotificationLink(notification);
                    return (
                      <li
                        key={notification.id}
                        className={cn(
                          'border-b',
                          'border-border',
                          'last:border-0',
                          'p-2',
                          'hover:bg-accent',
                          'notification-item'
                        )}
                      >
                        <NotificationItemWrapper
                          notificationLink={notificationLink}
                          notificationId={notification.id}
                          markAsRead={markAsRead}
                          onNavigate={() => setIsPopupOpen(false)}
                        >
                          <div className={cn('text-sm', 'mr-2')}>
                            <div className={cn('flex')}>
                              <p>
                                <span className={cn('font-semibold')}>
                                  {getFullName(notification?.createdBy)}
                                </span>
                                {` ${notification.message}`}
                              </p>
                            </div>
                            <p
                              className={cn(
                                'text-xs',
                                'text-placeholder',
                                'leading-[1.5rem]'
                              )}
                            >
                              {formatDate(notification.createdAt)}
                            </p>
                          </div>
                          <Button
                            variant="primary"
                            className={cn('h-7', 'text-xs', 'min-w-max')}
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              markAsRead(notification.id);
                            }}
                          >
                            Mark as read
                          </Button>
                        </NotificationItemWrapper>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface NotificationItemWrapperProps {
  notificationLink: string | null;
  notificationId: string;
  markAsRead: (notificationId: string) => void;
  onNavigate: () => void;
}

function NotificationItemWrapper({
  notificationLink,
  children,
  notificationId,
  markAsRead,
  onNavigate,
}: PropsWithChildren<NotificationItemWrapperProps>) {
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    markAsRead(notificationId);

    if (notificationLink) {
      navigate(notificationLink);
      onNavigate();
    }
  };

  if (!notificationLink)
    return (
      <button
        type="button"
        className={cn('flex', 'justify-between', 'w-full', 'text-left')}
        onClick={handleClick}
      >
        {children}
      </button>
    );
  return (
    <a
      href={notificationLink}
      className={cn('flex', 'justify-between')}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}
